import type { McqQuestion } from '../../../../types/index';
import { QUESTION_TYPE } from '../../../../constants/questionTypes';
import type { ApiQuestion } from '../../../../internalTypes';
import type { ResolvedQuestionShell } from '../shared/resolveQuestionShell';
import { parseChoiceRandomization } from '../shared/choiceRandomization';
import { applyMcqChoiceRandomization } from './applyMcqChoiceRandomization';
import { buildMcqOptions } from './buildMcqOptions';
import { extractAnswerConditions } from './extractAnswerConditions';
import { extractDefaultOptionIds } from './extractDefaultOptionIds';

export function mapMcqQuestion(
  raw: ApiQuestion,
  shell: ResolvedQuestionShell,
  language?: string
): McqQuestion {
  const configs = raw.questionConfigs ?? [];
  const mcqVariant = configs.find(c => c.key === 'MCQ_VARIANT')?.value?.[0]?.toUpperCase() ?? 'SINGLESELECT';
  const selectionMode = mcqVariant.includes('MULTI') ? 'multiple' : 'single';

  const formatType = configs.find(c => c.key === 'MCQ_FORMAT_TYPE')?.value?.[0]?.toUpperCase() ?? 'LIST';
  const displayFormat = formatType.includes('GRID') ? 'grid' : 'list';

  const columnsConfig = configs.find(c => c.key === 'COLUMNS')?.value?.[0];
  const columnCount = columnsConfig ? Number(columnsConfig) : undefined;

  const builtOptions = buildMcqOptions(raw, language);
  const defaultOptionIdsBeforeRandomization = extractDefaultOptionIds(
    configs,
    builtOptions.map(o => o.id)
  );
  const randomization = parseChoiceRandomization(configs);
  const { options, shuffleOptions } = applyMcqChoiceRandomization(
    builtOptions,
    randomization,
    defaultOptionIdsBeforeRandomization
  );
  const defaultOptionIds = extractDefaultOptionIds(
    configs,
    options.map(o => o.id)
  );
  const answerConditions = extractAnswerConditions(configs);

  return {
    ...shell,
    type: QUESTION_TYPE.MCQ,
    options,
    selectionMode,
    displayFormat,
    shuffleOptions,
    ...(columnCount !== undefined && Number.isFinite(columnCount) ? { columnCount } : {}),
    ...(defaultOptionIds ? { defaultOptionIds } : {}),
    ...answerConditions,
    ...(raw.additionalInfo?.hasOptionLevelAnswers ? { hasOptionLevelAnswers: true } : {}),
  };
}
